import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ScanLine } from "lucide-react";
import TicketScanSearch from "@/components/TicketScanSearch";
import { typeBadge } from "@/lib/ticketTypes";

export default function Tickets() {
  const [q, setQ] = useState("");
  const [ticketType, setTicketType] = useState("ALL");
  const [filterBy, setFilterBy] = useState("ALL");
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(0);
  const [scanning, setScanning] = useState(false);
  const limit = 50;

  const load = () => {
    const params = { limit, skip: page * limit };
    if (q) params.q = q;
    if (ticketType !== "ALL") params.ticket_type = ticketType;
    if (filterBy !== "ALL") params.filter_by = filterBy;
    api.get("/tickets", { params }).then((r) => setData(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketType, filterBy, page]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Manage</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">TICKETS</h1>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(0), load())}
            placeholder="Search name, ticket ID, barcode, email, phone…"
            className="pl-9 pr-11 bg-[#0B1120] border-slate-700 text-white h-11"
            data-testid="tickets-search"
          />
          <button type="button" onClick={() => setScanning(true)} title="Scan barcode" aria-label="Scan barcode"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md flex items-center justify-center text-orange-400 hover:bg-slate-800 hover:text-orange-300 transition-colors"
            data-testid="tickets-scan-btn">
            <ScanLine size={18} />
          </button>
        </div>
        {scanning && <TicketScanSearch onClose={() => setScanning(false)} />}
        <Select value={ticketType} onValueChange={(v) => { setTicketType(v); setPage(0); }}>
          <SelectTrigger className="w-full md:w-40 bg-[#0B1120] border-slate-700 h-11 text-white" data-testid="tickets-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Types</SelectItem>
            <SelectItem value="REGULAR">Regular</SelectItem>
            <SelectItem value="VIP">VIP</SelectItem>
            <SelectItem value="EMPLOYEE">Employee</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterBy} onValueChange={(v) => { setFilterBy(v); setPage(0); }}>
          <SelectTrigger className="w-full md:w-52 bg-[#0B1120] border-slate-700 h-11 text-white" data-testid="tickets-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive/Cancelled</SelectItem>
            <SelectItem value="REMAINING">Drinks Remaining</SelectItem>
            <SelectItem value="ZERO">Zero Drinks</SelectItem>
            <SelectItem value="NEVER_SCANNED">Never Scanned</SelectItem>
            <SelectItem value="FULLY_USED">Fully Used</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => { setPage(0); load(); }}
          className="h-11 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold"
          data-testid="tickets-search-btn"
        >
          Search
        </Button>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-widest text-slate-400 border-b border-slate-800 bg-black/20">
          <div className="col-span-2">Ticket ID</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Guest</div>
          <div className="col-span-2">Drinks</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Open</div>
        </div>
        <div className="divide-y divide-slate-800/60">
          {data.items.length === 0 && <div className="p-10 text-center text-slate-500">No tickets match.</div>}
          {data.items.map((t) => (
            <Link
              key={t.id}
              to={`/tickets/${t.id}`}
              className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-4 hover:bg-white/5 transition-colors items-center"
              data-testid={`ticket-row-${t.ticket_id}`}
            >
              <div className="md:col-span-2 font-mono-fb text-orange-300 font-bold">{t.ticket_id}</div>
              <div className="md:col-span-2">
                <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${typeBadge(t.ticket_type)}`} data-testid={`ticket-type-badge-${t.ticket_id}`}>
                  {t.ticket_type}
                </span>
              </div>
              <div className="md:col-span-3 text-slate-200 truncate">{t.guest_name || <span className="text-slate-500">— Unnamed —</span>}</div>
              <div className="md:col-span-2 text-slate-300 font-mono-fb">
                <span className="text-white font-bold">{t.drinks_remaining}</span>
                <span className="text-slate-500"> / {t.drinks_allowed}</span>
              </div>
              <div className="md:col-span-2">
                <span className={`text-xs font-bold ${
                  t.status === "ACTIVE" ? "text-emerald-400" : t.status === "INACTIVE" ? "text-slate-400" : "text-rose-400"}`}>
                  {t.status}
                </span>
              </div>
              <div className="md:col-span-1 text-right text-orange-400 text-sm font-bold">Open →</div>
            </Link>
          ))}
        </div>
        <div className="flex justify-between items-center p-4 text-sm text-slate-400 border-t border-slate-800">
          <div>Showing {data.items.length} of {data.total}</div>
          <div className="flex gap-2">
            <Button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} variant="outline" className="bg-slate-900 border-slate-700">Prev</Button>
            <Button disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)} variant="outline" className="bg-slate-900 border-slate-700">Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
